import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Lock, Eye, EyeOff, CheckCircle, Sparkles, AlertCircle, ArrowLeft } from 'lucide-react';

import { API_BASE_URL as API_BASE } from '@/services/api';

function getPasswordStrength(password: string): 'weak' | 'medium' | 'strong' {
  if (password.length < 8) return 'weak';
  let score = 0;
  if (/[a-z]/.test(password)) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;
  if (password.length >= 12) score++;
  if (score >= 4) return 'strong';
  if (score >= 2) return 'medium';
  return 'weak';
}

export function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error' | 'invalid_token'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const strength = useMemo(() => getPasswordStrength(newPassword), [newPassword]);

  useEffect(() => {
    if (!token) {
      setStatus('invalid_token');
    }
  }, [token]);

  // Auto-redirect to login after success
  useEffect(() => {
    if (status === 'success') {
      const timer = setTimeout(() => navigate('/login-traditional'), 3000);
      return () => clearTimeout(timer);
    }
  }, [status, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword.length < 8) {
      setErrorMessage(t('resetPassword.passwordTooShort', 'Password must be at least 8 characters'));
      setStatus('error');
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage(t('resetPassword.passwordMismatch', 'Passwords do not match'));
      setStatus('error');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await fetch(`${API_BASE}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        if (response.status === 400 || response.status === 404) {
          setStatus('invalid_token');
          return;
        }
        throw new Error(data.detail || t('resetPassword.resetFailed', 'Failed to reset password'));
      }

      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : t('common.somethingWentWrong', 'Something went wrong'));
    }
  };

  const strengthConfig = {
    weak: { color: 'bg-red-500', width: 'w-1/3', label: t('resetPassword.strength.weak', 'Weak') },
    medium: { color: 'bg-yellow-500', width: 'w-2/3', label: t('resetPassword.strength.medium', 'Medium') },
    strong: { color: 'bg-green-500', width: 'w-full', label: t('resetPassword.strength.strong', 'Strong') },
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-4">
        <Card className="border border-gray-200 shadow-sm bg-white">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-14 h-14 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center mb-3 shadow-lg">
              <Lock className="h-7 w-7 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">
              {t('resetPassword.title', 'Create new password')}
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              {t('resetPassword.subtitle', 'Enter your new password below')}
            </p>
          </CardHeader>

          <CardContent className="space-y-5 pt-2">
            {status === 'invalid_token' ? (
              <div className="text-center space-y-4 py-4">
                <Alert className="border-red-200 bg-red-50">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-700 text-sm">
                    {t('resetPassword.invalidToken', 'Invalid or expired reset link. Please request a new one.')}
                  </AlertDescription>
                </Alert>
                <Button
                  onClick={() => navigate('/auth/forgot-password')}
                  className="w-full h-11 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white font-semibold"
                >
                  {t('forgotPassword.sendReset', 'Send reset link')}
                </Button>
                <button
                  onClick={() => navigate('/login-traditional')}
                  className="text-sm text-gray-500 hover:text-primary-600 font-medium hover:underline transition-colors inline-flex items-center"
                >
                  <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
                  {t('forgotPassword.backToLogin', 'Back to login')}
                </button>
              </div>
            ) : status === 'success' ? (
              <div className="text-center space-y-4 py-4">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center animate-bounce">
                  <CheckCircle className="h-9 w-9 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('resetPassword.success', 'Password reset successfully!')}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {t('resetPassword.successDesc', 'You can now log in with your new password. Redirecting...')}
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {status === 'error' && errorMessage && (
                  <Alert className="border-red-200 bg-red-50">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-700 text-sm">
                      {errorMessage}
                    </AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="new-password" className="text-sm font-medium text-gray-700">
                    {t('resetPassword.newPassword', 'New password')}
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      id="new-password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pl-10 pr-10 h-12 border-2 border-gray-200 focus:border-primary-400 rounded-xl text-base"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>

                  {/* Password strength indicator */}
                  {newPassword.length > 0 && (
                    <div className="space-y-1">
                      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${strengthConfig[strength].color} ${strengthConfig[strength].width} rounded-full transition-all duration-300`}
                        />
                      </div>
                      <p className={`text-xs font-medium ${
                        strength === 'weak' ? 'text-red-600' : strength === 'medium' ? 'text-yellow-600' : 'text-green-600'
                      }`}>
                        {strengthConfig[strength].label}
                      </p>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="text-sm font-medium text-gray-700">
                    {t('resetPassword.confirmPassword', 'Confirm password')}
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      id="confirm-password"
                      type={showConfirm ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10 pr-10 h-12 border-2 border-gray-200 focus:border-primary-400 rounded-xl text-base"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white font-semibold text-base shadow-lg hover:shadow-xl transition-all transform hover:scale-[1.02] active:scale-[0.98]"
                  disabled={status === 'loading'}
                >
                  {status === 'loading' ? (
                    <span className="flex items-center">
                      <Sparkles className="mr-2 h-5 w-5 animate-spin" />
                      {t('resetPassword.resetting', 'Resetting...')}
                    </span>
                  ) : (
                    <span className="flex items-center">
                      {t('resetPassword.resetButton', 'Reset password')}
                      <CheckCircle className="ml-2 h-5 w-5" />
                    </span>
                  )}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => navigate('/login-traditional')}
                    className="text-sm text-gray-500 hover:text-primary-600 font-medium hover:underline transition-colors inline-flex items-center"
                  >
                    <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
                    {t('forgotPassword.backToLogin', 'Back to login')}
                  </button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default ResetPassword;
